import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw9210f1Api, F910, CreateRequest, UpdateRequest } from '../../../services/pw9210f1Api';

export type Mode = 'browse' | 'add' | 'edit';

const emptyForm = {
  f910_01: '', f910_02: '', f910_11: '', f910_13: '', f910_14: '',
  f910_15: '', f910_16: '', f910_17: '', f910_19: '', f910_20: '',
  f910_21: '', f910_22: '', f910_23: '', f910_24: '',
  f910_29: 0, f910_30: 0, f910_31: '', f910_32: '', f910_33: '',
};

function dateStr(d: string | null): string {
  if (!d) return '';
  return d.substring(0, 10);
}

export function useAttendanceForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F910[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const setField = (key: string, val: string | number) => setForm(f => ({ ...f, [key]: val }));

  // Load records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pw9210f1Api.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setForm({
        f910_01: current.f910_01, f910_02: dateStr(current.f910_02),
        f910_11: current.f910_11, f910_13: current.f910_13,
        f910_14: current.f910_14, f910_15: current.f910_15,
        f910_16: current.f910_16, f910_17: current.f910_17,
        f910_19: current.f910_19, f910_20: current.f910_20,
        f910_21: current.f910_21, f910_22: current.f910_22,
        f910_23: current.f910_23, f910_24: current.f910_24,
        f910_29: current.f910_29, f910_30: current.f910_30,
        f910_31: current.f910_31, f910_32: current.f910_32,
        f910_33: current.f910_33,
      });
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => { if (data.length > 0) setCurrentIdx(0); });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setForm({ ...emptyForm }); };
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setForm({
      f910_01: current.f910_01, f910_02: dateStr(current.f910_02),
      f910_11: current.f910_11, f910_13: current.f910_13,
      f910_14: current.f910_14, f910_15: current.f910_15,
      f910_16: current.f910_16, f910_17: current.f910_17,
      f910_19: current.f910_19, f910_20: current.f910_20,
      f910_21: current.f910_21, f910_22: current.f910_22,
      f910_23: current.f910_23, f910_24: current.f910_24,
      f910_29: current.f910_29, f910_30: current.f910_30,
      f910_31: current.f910_31, f910_32: current.f910_32,
      f910_33: current.f910_33,
    });
  };
  const cancel = () => {
    setMode('browse');
    if (current) {
      setForm({
        f910_01: current.f910_01, f910_02: dateStr(current.f910_02),
        f910_11: current.f910_11, f910_13: current.f910_13,
        f910_14: current.f910_14, f910_15: current.f910_15,
        f910_16: current.f910_16, f910_17: current.f910_17,
        f910_19: current.f910_19, f910_20: current.f910_20,
        f910_21: current.f910_21, f910_22: current.f910_22,
        f910_23: current.f910_23, f910_24: current.f910_24,
        f910_29: current.f910_29, f910_30: current.f910_30,
        f910_31: current.f910_31, f910_32: current.f910_32,
        f910_33: current.f910_33,
      });
    }
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        const req: CreateRequest = { ...form, f910_29: Number(form.f910_29), f910_30: Number(form.f910_30) };
        await pw9210f1Api.create(req);
        message.success('新增成功');
      } else if (current) {
        const req: UpdateRequest = {
          f910_11: form.f910_11, f910_13: form.f910_13, f910_14: form.f910_14,
          f910_15: form.f910_15, f910_16: form.f910_16, f910_17: form.f910_17,
          f910_19: form.f910_19, f910_20: form.f910_20, f910_21: form.f910_21,
          f910_22: form.f910_22, f910_23: form.f910_23, f910_24: form.f910_24,
          f910_29: Number(form.f910_29), f910_30: Number(form.f910_30),
          f910_31: form.f910_31, f910_32: form.f910_32, f910_33: form.f910_33,
        };
        await pw9210f1Api.update(current.f910_01, dateStr(current.f910_02), req);
        message.success('修改成功');
      }
      const data = await loadRecords();
      // Re-position
      const targetEmp = mode === 'add' ? form.f910_01 : current?.f910_01 || '';
      const targetDate = mode === 'add' ? form.f910_02 : dateStr(current?.f910_02 ?? null);
      const idx = data.findIndex(r => r.f910_01 === targetEmp && dateStr(r.f910_02) === targetDate);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await pw9210f1Api.delete(current.f910_01, dateStr(current.f910_02));
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r =>
      r.f910_01.includes(searchText) || r.f910_14.includes(searchText)
    );
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    form, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch, loadRecords,
  };
}
